import React from 'react';
import useConfig from '../hooks/useConfig';
import { generateCodeVerifier, generateCodeChallenge, generateState } from '../utils/pkce';

export default function Login({ autoStart = false, attemptId }) {
  const { config, loading, error } = useConfig();
  const [loginError, setLoginError] = React.useState(null);
  const autoStartedRef = React.useRef(false);

  const handleLogin = React.useCallback(async () => {
    if (!config) return;

    try {
      const state = attemptId || generateState();
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_code_verifier', codeVerifier);

      const params = new URLSearchParams({
        scope: config.scope,
        client_id: config.clientId,
        response_type: config.responseType,
        redirect_uri: config.redirectUri,
        state,
      });

      const authUrl = `${config.authEndpoint}?${params.toString()}`;
      window.location.href = authUrl;
    } catch (err) {
      setLoginError(err.message || 'Failed to initiate login. Ensure you are using a secure (HTTPS) connection.');
    }
  }, [config, attemptId]);

  React.useEffect(() => {
    if (autoStart && config && !autoStartedRef.current) {
      autoStartedRef.current = true;
      handleLogin();
    }
  }, [autoStart, config, handleLogin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <p className="text-white text-lg">Loading configuration...</p>
      </div>
    );
  }

  if (autoStart && !error && !loginError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
        <div
          role="status"
          aria-label="Redirecting to Operator sign-in"
          className="w-12 h-12 border-4 border-gray-600 border-t-green-500 rounded-full animate-spin"
        />
        <span className="sr-only">Redirecting to Operator sign-in...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-red-800 text-white p-6 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Configuration Error</h2>
          <p>{error}</p>
          <p className="mt-2 text-sm text-red-200">
            Ensure your <code>.env.local</code> or <code>.env.development</code> file is configured.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="bg-gray-800 shadow-xl rounded-2xl p-10 max-w-md w-full text-center">
        <h1 className="text-3xl font-bold text-white mb-2">Operator Auth</h1>
        <p className="text-gray-400 mb-8">
          OAuth 2.0 Authorization Code Flow
        </p>

        <button
          onClick={handleLogin}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 text-lg"
        >
          Sign in with Operator
        </button>

        {loginError && (
          <div className="mt-4 bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-3">
            <p className="text-red-300 text-sm">{loginError}</p>
          </div>
        )}

        <div className="mt-8 text-left text-xs text-gray-500 space-y-1">
          <p><span className="font-semibold text-gray-400">Auth Endpoint:</span> {config.authEndpoint}</p>
          <p><span className="font-semibold text-gray-400">Client ID:</span> {config.clientId}</p>
          <p><span className="font-semibold text-gray-400">Redirect URI:</span> {config.redirectUri}</p>
          <p><span className="font-semibold text-gray-400">Scope:</span> {config.scope}</p>
        </div>
      </div>
    </div>
  );
}
